// src/pages/leads/useLeadsPage.ts
import { useEffect, useMemo, useState } from "react";

import { useLeads, useDeleteLeadMutation } from "@/api/features/leads";
import { useUsers } from "@/api/features/users/queries";

import type { LeadListItem, PaginatedLeads } from "@/api/features/leads";
import type { PaginatedUsers, UserResult } from "@/api/features/users/types";

/* --------------------------------- UI Types --------------------------------- */

export type LeadStatus = "Pending" | "Completed" | "Cancelled";

export type LeadSource =
  | "Walk-in"
  | "Referral"
  | "Online"
  | "Advertisement"
  | "Social Media"
  | "Partner"
  | "Phone Inquiry"
  | "Email Campaign"
  | "Other";

export type Priority = "Low" | "Medium" | "High" | "Urgent";

export type TransferRecord = {
  at: string; // ISO datetime
  fromEmployee: string;
  toEmployee: string;
  reason?: string;
};

export type LeadRow = {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  date: string; // yyyy-mm-dd
  products: string[];
  status: LeadStatus;
  reason?: string;
  employee: string;
  employeeId: number | null;
  source?: LeadSource;
  priority?: Priority;
  notes?: string;
  transferHistory?: TransferRecord[];
};

export type LeadTableFilters = {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  status?: LeadStatus | "";
  employeeId?: string | "";
  product?: string;
  dateFrom?: string;
  dateTo?: string;
  source?: LeadSource | "";
  priority?: Priority | "";
};

/* --------------------------------- Mappers --------------------------------- */

function mapStatusToUi(
  status: LeadListItem["status"] | null | undefined,
): LeadStatus {
  const s = String(status ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");

  if (s === "COMPLETED") return "Completed";
  if (s === "CANCELLED" || s === "CANCELED") return "Cancelled";
  return "Pending";
}

function mapSourceToUi(
  source: LeadListItem["source"] | null | undefined,
): LeadSource | undefined {
  switch (source) {
    case "WALK_IN":
      return "Walk-in";
    case "REFERRAL":
      return "Referral";
    case "ONLINE":
      return "Online";
    case "ADVERTISEMENT":
      return "Advertisement";
    case "SOCIAL_MEDIA":
      return "Social Media";
    case "PARTNER":
      return "Partner";
    case "PHONE_INQUIRY":
      return "Phone Inquiry";
    case "EMAIL_CAMPAIGN":
      return "Email Campaign";
    case "OTHER":
      return "Other";
    default:
      return undefined;
  }
}

function mapPriorityToUi(
  priority: LeadListItem["priority"] | null | undefined,
): Priority | undefined {
  switch (priority) {
    case "LOW":
      return "Low";
    case "MEDIUM":
      return "Medium";
    case "HIGH":
      return "High";
    case "URGENT":
      return "Urgent";
    default:
      return undefined;
  }
}

/**
 * Date handling:
 * - If backend returns "YYYY-MM-DD" → keep it (no timezone conversion)
 * - If backend returns ISO "YYYY-MM-DDT..." → take the date part only
 */
function formatDateYmd(input: unknown): string {
  if (!input) return "";
  const s = String(input);

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];

  return "";
}

function mapApiLeadToUi(row: LeadListItem): LeadRow {
  const products: string[] = [];
  const primary = row.primaryProductName ?? "";

  if (primary) {
    if (row.productsCount && row.productsCount > 1) products.push(`${primary} +`);
    else products.push(primary);
  }

  return {
    id: String(row.id),
    firstName: row.firstName,
    lastName: row.lastName,
    phone: row.phone,
    email: row.email,
    date: formatDateYmd(row.leadDate),
    products,
    status: mapStatusToUi(row.status),
    reason: "",
    employee: row.assignedToUserName ?? "",
    employeeId: row.assignedToUserId ?? null,
    source: mapSourceToUi(row.source ?? undefined),
    priority: mapPriorityToUi(row.priority ?? undefined),
    notes: "",
    transferHistory: [],
  };
}

function mapSourceToApi(source: LeadSource | "" | undefined) {
  switch (source) {
    case "Walk-in":
      return "WALK_IN";
    case "Referral":
      return "REFERRAL";
    case "Online":
      return "ONLINE";
    case "Advertisement":
      return "ADVERTISEMENT";
    case "Social Media":
      return "SOCIAL_MEDIA";
    case "Partner":
      return "PARTNER";
    case "Phone Inquiry":
      return "PHONE_INQUIRY";
    case "Email Campaign":
      return "EMAIL_CAMPAIGN";
    case "Other":
      return "OTHER";
    default:
      return undefined;
  }
}

/* --------------------------------- Hook ----------------------------------- */

export function useLeadsPage() {
  // ✅ default to Pending tab
  const [filters, setFilters] = useState<LeadTableFilters>({
    status: "Pending",
  });

  const [globalQ, setGlobalQ] = useState("");
  const [page, setPage] = useState(1);

  // UI page size (what you show per page)
  const pageSize = 10;

  const serverStatus =
    filters.status && filters.status.length
      ? filters.status === "Pending"
        ? "PENDING"
        : filters.status === "Completed"
          ? "COMPLETED"
          : "CANCELLED"
      : undefined;

  const serverPriority =
    filters.priority === "Low"
      ? "LOW"
      : filters.priority === "Medium"
        ? "MEDIUM"
        : filters.priority === "High"
          ? "HIGH"
          : filters.priority === "Urgent"
            ? "URGENT"
            : undefined;
  const serverSource = mapSourceToApi(filters.source);
  const baseLeadQuery = {
    q: globalQ.trim() ? globalQ.trim() : undefined,
    priority: serverPriority as any,
    source: serverSource as any,
    assignedToUserId:
      filters.employeeId && Number.isFinite(Number(filters.employeeId))
        ? Number(filters.employeeId)
        : undefined,
    firstName: filters.firstName?.trim() || undefined,
    lastName: filters.lastName?.trim() || undefined,
    phone: filters.phone?.trim() || undefined,
    email: filters.email?.trim() || undefined,
    product: filters.product?.trim() || undefined,
    leadDateFrom: filters.dateFrom || undefined,
    leadDateTo: filters.dateTo || undefined,
  };

  const { data: usersData } = useUsers({
    page: 1,
    limit: 200,
  });

  const { data, isLoading, isError } = useLeads({
    page,
    pageSize,
    status: serverStatus,
    ...baseLeadQuery,
  });

  // For CSV export: fetch all filtered rows (not just current page).
  const { data: exportData, isLoading: isExportLoading } = useLeads({
    page: 1,
    pageSize: 10000,
    status: serverStatus,
    ...baseLeadQuery,
  });

  const { data: pendingCountsData } = useLeads({
    page: 1,
    pageSize: 1,
    status: "PENDING",
    ...baseLeadQuery,
  });
  const { data: completedCountsData } = useLeads({
    page: 1,
    pageSize: 1,
    status: "COMPLETED",
    ...baseLeadQuery,
  });
  const { data: cancelledCountsData } = useLeads({
    page: 1,
    pageSize: 1,
    status: "CANCELLED",
    ...baseLeadQuery,
  });

  const deleteMutation = useDeleteLeadMutation();

  const pageData = data as PaginatedLeads | undefined;

  // ✅ these should come from backend
  const serverItems = pageData?.items ?? [];
  const serverTotal = pageData?.total ?? 0;
  const serverPage = pageData?.page ?? page;
  const serverPageSize = pageData?.pageSize ?? pageSize;

  // map
  const rows: LeadRow[] = useMemo(
    () => serverItems.map(mapApiLeadToUi),
    [serverItems],
  );
  const exportRows: LeadRow[] = useMemo(
    () => ((exportData as PaginatedLeads | undefined)?.items ?? []).map(mapApiLeadToUi),
    [exportData],
  );

  // ✅ pagination should reflect SERVER totals
  const totalFiltered = serverTotal;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / serverPageSize));

  // When filters/search change, reset to page 1
  useEffect(() => {
    setPage(1);
  }, [
    filters.status,
    filters.firstName,
    filters.lastName,
    filters.phone,
    filters.email,
    filters.employeeId,
    filters.product,
    filters.dateFrom,
    filters.dateTo,
    filters.source,
    filters.priority,
    globalQ,
  ]);

  // What to render on this page
  const pageRows = rows;
  const start = (serverPage - 1) * serverPageSize;
  const serialStart = start;

  const employeeOptions = useMemo(
    () =>
      ((usersData as PaginatedUsers | undefined)?.items ?? []).map((u: UserResult) => {
        const fullName = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
        return {
          value: String(u.id),
          label: fullName || u.email || u.username || `User #${u.id}`,
        };
      }),
    [usersData]
  );

  const statusCounts = useMemo<Record<LeadStatus, number>>(() => {
    return {
      Pending: pendingCountsData?.total ?? 0,
      Completed: completedCountsData?.total ?? 0,
      Cancelled: cancelledCountsData?.total ?? 0,
    };
  }, [cancelledCountsData?.total, completedCountsData?.total, pendingCountsData?.total]);

  function deleteLead(id: string | number) {
    deleteMutation.mutate(id);
  }

  return {
    // data / states
    rows,
    pageRows,
    serialStart,
    page: serverPage,
    pageSize: serverPageSize,
    start,
    totalFiltered,
    totalPages,

    // loading
    isLoading,
    isExportLoading,
    isError,
    deleteLoading: deleteMutation.isPending,

    // filters & search
    filters,
    setFilters,
    globalQ,
    setGlobalQ,
    employeeOptions,
    statusCounts,

    // pagination actions
    setPage,

    // actions
    deleteLead,
    exportRows,
  };
}
